<template>
  <main class="activate-page">
    <div class="activate-layout">
      <!-- LEFT: FORM -->
      <section class="form-panel">
        <div class="form-content">
          <button
            v-if="step === 'form'"
            type="button"
            class="back-link"
            @click="$router.push('/login')"
          >
            <span class="back-arrow" aria-hidden="true">←</span>
            Kembali ke Login
          </button>

          <button
            v-else
            type="button"
            class="back-link"
            :disabled="loading"
            @click="backToForm"
          >
            <span class="back-arrow" aria-hidden="true">←</span>
            Kembali ke formulir
          </button>

          <div class="icon-badge" aria-hidden="true">
            <svg viewBox="0 0 24 24" fill="none">
              <path
                d="M12 3 4.5 6v6c0 4.5 3.2 7.6 7.5 9 4.3-1.4 7.5-4.5 7.5-9V6L12 3Z"
                stroke="currentColor"
                stroke-width="1.7"
                stroke-linecap="round"
                stroke-linejoin="round"
              />
              <path
                d="M9.5 12.2 11.2 14l3.6-3.7"
                stroke="currentColor"
                stroke-width="1.7"
                stroke-linecap="round"
                stroke-linejoin="round"
              />
            </svg>
          </div>

          <h1 class="title">
            {{ step === 'form' ? 'Aktivasi Akun' : 'Verifikasi Email' }}
          </h1>

          <p class="description">
            <template v-if="step === 'form'">
              Masukkan kode aktivasi dari booklet DITAP, lalu buat akun
              untuk mengelola landing page kamu.
            </template>
            <template v-else>
              Masukkan kode OTP 6 digit yang kami kirim ke email yang kamu
              daftarkan.
            </template>
          </p>

          <!-- STEP 1: FORM -->
          <form v-if="step === 'form'" @submit.prevent="activateAccount">
            <div class="form-group">
              <label for="code">Kode Aktivasi</label>
              <input
                id="code"
                v-model.trim="form.code"
                type="text"
                placeholder="Contoh: ZXK7WB"
                minlength="6"
                maxlength="6"
                pattern="[A-Za-z0-9]{6}"
                autocomplete="off"
                autocapitalize="characters"
                required
                :disabled="loading"
                @input="
                  form.code = form.code
                    .toUpperCase()
                    .replace(/[^A-Z0-9]/g, '')
                    .slice(0, 6)
                "
              />
            </div>

            <div class="form-group">
              <label for="name">Nama</label>
              <input
                id="name"
                v-model.trim="form.name"
                type="text"
                placeholder="Masukkan nama kamu"
                maxlength="255"
                autocomplete="name"
                required
                :disabled="loading"
              />
            </div>

            <div class="form-group">
              <label for="email">Email</label>
              <input
                id="email"
                v-model.trim="form.email"
                type="email"
                placeholder="nama@email.com"
                maxlength="255"
                autocomplete="email"
                required
                :disabled="loading"
              />
              <small class="field-hint">
                Gunakan email yang bisa kamu akses untuk menerima OTP.
              </small>
            </div>

            <div class="form-group">
              <label for="password">Password</label>
              <input
                id="password"
                v-model="form.password"
                type="password"
                placeholder="Minimal 8 karakter"
                minlength="8"
                autocomplete="new-password"
                required
                :disabled="loading"
              />
            </div>

            <div class="form-group">
              <label for="password_confirmation">
                Konfirmasi Password
              </label>
              <input
                id="password_confirmation"
                v-model="form.password_confirmation"
                type="password"
                placeholder="Ulangi password"
                minlength="8"
                autocomplete="new-password"
                required
                :disabled="loading"
              />
            </div>

            <button type="submit" class="submit-btn" :disabled="loading">
              {{ loading ? 'Mengirim OTP...' : 'Kirim Kode OTP' }}
            </button>
          </form>

          <!-- STEP 2: OTP -->
          <form v-else @submit.prevent="verifyOtp">
            <div class="form-group">
              <label for="otp">Kode OTP</label>
              <input
                id="otp"
                v-model.trim="otp"
                type="text"
                inputmode="numeric"
                autocomplete="one-time-code"
                placeholder="Masukkan 6 digit OTP"
                minlength="6"
                maxlength="6"
                pattern="[0-9]{6}"
                required
                :disabled="loading"
                @input="otp = otp.replace(/\D/g, '').slice(0, 6)"
              />
              <small class="field-hint">Kode berlaku selama 10 menit.</small>
            </div>

            <button type="submit" class="submit-btn" :disabled="loading">
              {{
                loading
                  ? 'Memverifikasi...'
                  : 'Verifikasi dan Aktifkan Akun'
              }}
            </button>

            <button
              type="button"
              class="submit-btn ghost-btn"
              :disabled="loading"
              @click="resendOtp"
            >
              Kirim Ulang OTP
            </button>
          </form>

          <p class="footer-text">
            Sudah punya akun?
            <RouterLink to="/login">Login</RouterLink>
          </p>
        </div>
      </section>

      <!-- RIGHT: IMAGE -->
      <aside class="brand-panel">
        <img
          v-if="logoPath"
          :src="logoPath"
          alt="DITAP"
          class="brand-image"
        />
      </aside>
    </div>
  </main>
</template>
<script>
import axios from '@/services/axios'
import { useToast } from 'vue-toastification'

import { useSeoFromI18n } from '@/composables/useSeo'

export default {
  name: 'ActivateAccount',

  setup() {
    const toast = useToast()

    // ===== SEO meta (noindex) =====
    useSeoFromI18n('meta.activateAccount', {
      robots: 'noindex, nofollow'
    })

    return { toast }
  },

  data() {
    return {
      step: 'form',
      loading: false,
      otp: '',
      logoPath: '',

      form: {
        code: '',
        name: '',
        email: '',
        password: '',
        password_confirmation: ''
      }
    }
  },

  mounted() {
    this.loadBranding()
  },

  methods: {
    async loadBranding() {
      try {
        const response = await axios.get('/public/branding')
        const data = response?.data?.data ?? response?.data ?? {}
        this.logoPath = data.logo_url || ''
      } catch (error) {
        console.warn('Gagal memuat branding:', error)
      }
    },

    handleError(error, fallback) {
      const response = error.response
      const data = response?.data

      const mainMessage = data?.message || fallback

      const errors = data?.errors
      if (errors && typeof errors === 'object') {
        const messages = Object.values(errors).flat()
        const firstMessage = messages[0]
        if (firstMessage) {
          this.toast.error(firstMessage, { timeout: 5000 })
          return
        }
      }

      this.toast.error(mainMessage, { timeout: 5000 })
    },

    async activateAccount() {
      if (this.loading) return

      this.loading = true

      const code = this.form.code.trim().toUpperCase()
      const email = this.form.email.trim().toLowerCase()

      if (!/^[A-Z0-9]{6}$/.test(code)) {
        this.toast.error(
          'Kode aktivasi harus terdiri dari 6 karakter huruf atau angka.',
          { timeout: 5000 }
        )
        this.loading = false
        return
      }

      if (this.form.password !== this.form.password_confirmation) {
        this.toast.error('Konfirmasi password tidak cocok.', {
          timeout: 5000
        })
        this.loading = false
        return
      }

      try {
        const response = await axios.post('/activate-account', {
          code,
          name: this.form.name.trim(),
          email,
          password: this.form.password,
          password_confirmation: this.form.password_confirmation
        })

        this.form.code = code
        this.form.email = email
        this.step = 'otp'
        this.otp = ''

        this.toast.success(
          response.data.message || 'OTP berhasil dikirim ke email kamu.',
          { timeout: 5000 }
        )
      } catch (error) {
        this.handleError(
          error,
          'Gagal memulai aktivasi. Silakan coba lagi.'
        )
      } finally {
        this.loading = false
      }
    },

    async verifyOtp() {
      if (this.loading) return

      this.loading = true

      if (!/^\d{6}$/.test(this.otp)) {
        this.toast.error('OTP harus terdiri dari 6 angka.', {
          timeout: 5000
        })
        this.loading = false
        return
      }

      try {
        const response = await axios.post(
          '/activate-account/verify-otp',
          {
            code: this.form.code,
            otp: this.otp
          }
        )

        this.toast.success(
          response.data.message || 'Akun berhasil diaktifkan.',
          { timeout: 5000 }
        )

        this.otp = ''
        this.form.password = ''
        this.form.password_confirmation = ''

        setTimeout(() => {
          this.$router.push('/login')
        }, 1500)
      } catch (error) {
        this.handleError(
          error,
          'Verifikasi OTP gagal. Silakan coba lagi.'
        )
      } finally {
        this.loading = false
      }
    },

    async resendOtp() {
      if (this.loading) return

      this.loading = true

      try {
        const response = await axios.post(
          '/activate-account/resend-otp',
          { code: this.form.code }
        )

        this.otp = ''

        this.toast.success(
          response.data.message || 'OTP baru berhasil dikirim.',
          { timeout: 5000 }
        )
      } catch (error) {
        this.handleError(
          error,
          'Gagal mengirim ulang OTP. Silakan coba lagi nanti.'
        )
      } finally {
        this.loading = false
      }
    },

    backToForm() {
      if (this.loading) return
      this.otp = ''
      this.step = 'form'
    }
  }
}
</script>

<style scoped>
/* ============================================================
   PAGE — halaman yang scroll, card tidak dikunci tinggi
   ============================================================ */
.activate-page {
  --primary: #0f58ff;
  --primary-dark: #0847dc;
  --turquoise: #00cfc1;
  --ink: #13213b;
  --muted: #78849b;
  --panel-bg: #0b1b42;

  display: flex;
  align-items: flex-start;
  justify-content: center;

  width: 100%;
  min-height: 100vh;
  min-height: 100dvh;

  padding:
    max(16px, env(safe-area-inset-top))
    max(16px, env(safe-area-inset-right))
    max(16px, env(safe-area-inset-bottom))
    max(16px, env(safe-area-inset-left));

  box-sizing: border-box;
  overflow-x: hidden;
  overflow-y: auto;

  background:
    radial-gradient(
      circle at 5% 5%,
      rgba(15, 88, 255, 0.07),
      transparent 30%
    ),
    radial-gradient(
      circle at 95% 95%,
      rgba(0, 207, 193, 0.08),
      transparent 28%
    ),
    #f4f7fc;

  color: var(--ink);
  /* font-family:
    Inter,
    -apple-system,
    BlinkMacSystemFont,
    "Segoe UI",
    sans-serif; */
}

/* ============================================================
   CARD — tinggi mengikuti konten, tidak dikunci
   ============================================================ */
.activate-layout {
  display: grid;
  grid-template-columns: minmax(0, 1fr) minmax(0, 1fr);

  width: 100%;
  max-width: 1080px;

  min-height: 0;
  margin: auto 0;

  overflow: hidden;

  border: 1px solid #e2e9f4;
  border-radius: 24px;
  background: #ffffff;

  box-shadow:
    0 24px 64px rgba(25, 48, 91, 0.10),
    0 3px 12px rgba(25, 48, 91, 0.035);
}

/* ============================================================
   LEFT FORM PANEL
   ============================================================ */
.form-panel {
  display: flex;
  flex-direction: column;
  justify-content: center;

  min-width: 0;
  min-height: 0;

  overflow: visible;

  padding: clamp(20px, 3vh, 40px) clamp(24px, 4vw, 56px);

  background: #ffffff;
}

.form-content {
  width: 100%;
  max-width: 380px;
  margin: auto;
}

/* Back link */
.back-link {
  display: inline-flex;
  align-items: center;
  gap: 6px;

  border: 0;
  background: transparent;
  padding: 0;
  margin-bottom: clamp(14px, 2.2vh, 24px);

  color: var(--muted);
  font: inherit;
  font-size: 13px;
  font-weight: 500;
  cursor: pointer;

  transition: color 0.15s ease;
}

.back-link:hover:not(:disabled) {
  color: var(--primary);
}

.back-link:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.back-arrow {
  font-size: 15px;
  line-height: 1;
}

/* Icon badge */
.icon-badge {
  width: 52px;
  height: 52px;
  display: grid;
  place-items: center;

  border-radius: 14px;
  background: rgba(15, 88, 255, 0.10);
  color: var(--primary);

  margin-bottom: clamp(12px, 2vh, 20px);
}

.icon-badge svg {
  width: 26px;
  height: 26px;
}

/* Title & description */
.title {
  margin: 0 0 10px;
  font-size: clamp(22px, 2.4vw, 28px);
  font-weight: 700;
  line-height: 1.25;
  color: var(--ink);
  letter-spacing: -0.01em;
}

.description {
  margin: 0 0 clamp(16px, 2.4vh, 24px);
  color: var(--muted);
  line-height: 1.65;
  font-size: 14px;
}

/* Form */
.form-group {
  display: flex;
  flex-direction: column;
  gap: 8px;
  margin-bottom: clamp(12px, 1.8vh, 18px);
}

.form-group label {
  font-size: 13px;
  font-weight: 600;
  color: var(--ink);
}

.form-group input {
  width: 100%;
  box-sizing: border-box;
  min-height: 46px;
  padding: 11px 14px;

  border: 1px solid #d1d5db;
  border-radius: 10px;
  background: #ffffff;
  color: var(--ink);
  font: inherit;
  font-size: 16px;
  outline: none;

  transition: border-color 0.15s ease, box-shadow 0.15s ease;
}

.form-group input:focus {
  border-color: var(--primary);
  box-shadow: 0 0 0 3px rgba(15, 88, 255, 0.12);
}

.form-group input:disabled {
  background: #f8fafc;
  cursor: not-allowed;
}

.field-hint {
  color: var(--muted);
  font-size: 12px;
  line-height: 1.55;
}

/* Submit */
.submit-btn {
  width: 100%;
  min-height: 48px;
  padding: 12px 16px;

  border: 0;
  border-radius: 10px;
  background: var(--primary);
  color: #ffffff;

  font: inherit;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;

  transition: background 0.15s ease, border-color 0.15s ease,
    color 0.15s ease;
}

.submit-btn:hover:not(:disabled) {
  background: var(--primary-dark);
}

.submit-btn:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.ghost-btn {
  margin-top: 10px;
  background: #ffffff;
  color: var(--primary);
  border: 1px solid #c7d6ff;
}

.ghost-btn:hover:not(:disabled) {
  background: #f2f6ff;
  border-color: var(--primary);
}

/* Footer text */
.footer-text {
  margin: clamp(16px, 2.4vh, 24px) 0 0;
  text-align: center;
  font-size: 13px;
  color: var(--muted);
}

.footer-text a {
  color: var(--primary);
  font-weight: 600;
  text-decoration: none;
}

.footer-text a:hover {
  text-decoration: underline;
}

/* ============================================================
   RIGHT IMAGE PANEL
   ============================================================ */
.brand-panel {
  position: relative;
  min-width: 0;
  height: 100%;
  min-height: 0;
  overflow: hidden;
  background: var(--panel-bg);

  display: flex;
  align-items: center;
  justify-content: center;
}

.brand-image {
  display: block;

  width: 100%;
  height: 100%;
  max-width: 100%;
  max-height: 100%;

  object-fit: contain;
  object-position: center;

  padding: clamp(16px, 4vw, 40px);
  box-sizing: border-box;
}

/* ============================================================
   LAPTOP BESAR (≤ 1200px)
   ============================================================ */
@media (max-width: 1200px) {
  .activate-layout {
    max-width: 960px;
  }

  .form-content {
    max-width: 360px;
  }
}

/* ============================================================
   TABLET (≤ 900px)
   ============================================================ */
@media (max-width: 900px) {
  .activate-layout {
    grid-template-columns: minmax(0, 1.1fr) minmax(0, 0.9fr);
    max-width: 100%;
    border-radius: 20px;
  }

  .form-panel {
    padding: clamp(18px, 2.6vh, 30px) clamp(20px, 3.2vw, 32px);
  }

  .form-content {
    max-width: 340px;
  }

  .brand-image {
    padding: clamp(12px, 3vw, 24px);
  }
}

/* ============================================================
   MOBILE BESAR (≤ 720px) — gambar di atas form
   ============================================================ */
@media (max-width: 720px) {
  .activate-page {
    display: block;
    min-height: 100dvh;
    padding: 0;
    background: #ffffff;
  }

  .activate-layout {
    display: flex;
    flex-direction: column-reverse;

    width: 100%;
    max-width: 100%;

    border: 0;
    border-radius: 0;
    box-shadow: none;
    background: #ffffff;
    margin: 0;
  }

  .brand-panel {
    width: 100%;
    height: 180px;
    min-height: 180px;
    flex: 0 0 180px;
  }

  .brand-image {
    height: 100%;
    max-height: 180px;
    padding: 14px;
  }

  .form-panel {
    flex: 1;
    justify-content: flex-start;
    overflow: visible;
    padding:
      26px
      max(20px, env(safe-area-inset-right))
      28px
      max(20px, env(safe-area-inset-left));
  }

  .form-content {
    max-width: 460px;
    margin: 0 auto;
  }

  .title {
    font-size: 24px;
  }

  .description {
    font-size: 14px;
  }
}

/* ============================================================
   MOBILE KECIL (≤ 480px)
   ============================================================ */
@media (max-width: 480px) {
  .brand-panel {
    height: 150px;
    min-height: 150px;
    flex-basis: 150px;
  }

  .brand-image {
    max-height: 150px;
    padding: 10px;
  }

  .form-panel {
    padding: 22px 16px 24px;
  }

  .form-content {
    max-width: 100%;
  }

  .icon-badge {
    width: 46px;
    height: 46px;
    border-radius: 12px;
    margin-bottom: 14px;
  }

  .icon-badge svg {
    width: 22px;
    height: 22px;
  }

  .title {
    font-size: 22px;
  }

  .description {
    font-size: 13.5px;
    margin-bottom: 16px;
  }

  .form-group {
    margin-bottom: 12px;
  }

  .form-group input {
    min-height: 44px;
    padding: 10px 12px;
    border-radius: 9px;
  }

  .submit-btn {
    min-height: 46px;
    border-radius: 9px;
  }
}

/* ============================================================
   MOBILE SANGAT KECIL (≤ 360px)
   ============================================================ */
@media (max-width: 360px) {
  .brand-panel {
    height: 130px;
    min-height: 130px;
    flex-basis: 130px;
  }

  .brand-image {
    max-height: 130px;
    padding: 8px;
  }

  .form-panel {
    padding: 20px 14px 22px;
  }

  .title {
    font-size: 20px;
  }

  .description {
    font-size: 13px;
  }

  .form-group label {
    font-size: 12.5px;
  }

  .form-group input {
    font-size: 15px;
    min-height: 42px;
  }

  .field-hint {
    font-size: 11.5px;
  }
}

/* ============================================================
   LANDSCAPE PENDEK
   ============================================================ */
@media (max-height: 520px) and (min-width: 721px) {
  .activate-page {
    padding: 8px;
  }

  .activate-layout {
    border-radius: 16px;
  }

  .form-panel {
    justify-content: flex-start;
    padding: 16px 24px;
  }

  .form-content {
    max-width: 100%;
    margin: 0;
  }

  .icon-badge {
    width: 40px;
    height: 40px;
    border-radius: 10px;
    margin-bottom: 10px;
  }

  .icon-badge svg {
    width: 20px;
    height: 20px;
  }

  .title {
    font-size: 20px;
    margin-bottom: 6px;
  }

  .description {
    font-size: 13px;
    margin-bottom: 12px;
  }

  .form-group {
    margin-bottom: 10px;
  }

  .form-group input {
    min-height: 40px;
    padding: 8px 12px;
  }

  .submit-btn {
    min-height: 42px;
  }
}

/* ============================================================
   ACCESSIBILITY
   ============================================================ */
@media (prefers-reduced-motion: reduce) {
  *,
  *::before,
  *::after {
    scroll-behavior: auto !important;
    animation-duration: 0.01ms !important;
    transition-duration: 0.01ms !important;
  }
}
</style>